import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  FileUp,
  Package,
  Pencil,
  Plus,
  Power,
  Search,
  Tags,
} from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { coincideBusqueda, pasaEstado, type FiltroEstado } from "./filtros";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import { ImportarPlanillaModal } from "../../components/ImportarPlanillaModal";
import {
  Badge,
  Button,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarCategoria,
  actualizarInsumo,
  crearCategoria,
  crearInsumo,
  descargarPlantillaInsumos,
  importarInsumos,
  listarAlertas,
  listarCategorias,
  listarInsumos,
  type AlertaStock,
  type CategoriaCreate,
  type CategoriaRead,
  type CategoriaUpdate,
  type InsumoCreate,
  type InsumoRead,
  type InsumoUpdate,
} from "./api";

const UNIDADES = ["Un", "Kg", "Lt", "Mt", "Saco", "Caja", "Rollo"] as const;

export function InsumosListPage(): JSX.Element {
  const [editing, setEditing] = useState<InsumoRead | "new" | null>(null);
  const [categoriasOpen, setCategoriasOpen] = useState(false);
  const [importando, setImportando] = useState(false);
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [estado, setEstado] = useState<FiltroEstado>("activos");
  const [soloBajoStock, setSoloBajoStock] = useState(false);

  const insumosQ = useQuery({
    queryKey: ["inventario", "insumos"],
    queryFn: listarInsumos,
  });
  const categoriasQ = useQuery({
    queryKey: ["inventario", "categorias"],
    queryFn: listarCategorias,
  });
  // Las alertas traen el stock total actual (server-side) de los insumos bajo
  // mínimo — el listado de insumos no incluye stock, así que es la fuente para
  // el filtro "bajo stock" y el badge de la fila.
  const alertasQ = useQuery({
    queryKey: ["inventario", "alertas"],
    queryFn: listarAlertas,
  });

  const categorias = categoriasQ.data ?? [];
  const nombrePorId = new Map<number, string>(
    categorias.map((c) => [c.id, c.nombre]),
  );
  const alertaPorInsumo = useMemo(() => {
    const m = new Map<number, AlertaStock>();
    for (const a of alertasQ.data ?? []) m.set(a.insumo_id, a);
    return m;
  }, [alertasQ.data]);
  const nBajoStock = alertaPorInsumo.size;

  const total = insumosQ.data?.length ?? 0;
  const filtrados = useMemo(() => {
    const data = insumosQ.data ?? [];
    return data.filter((i) => {
      if (!pasaEstado(i.activo, estado)) return false;
      if (categoriaFiltro && String(i.categoria_id ?? "") !== categoriaFiltro)
        return false;
      if (soloBajoStock && !alertaPorInsumo.has(i.id)) return false;
      return coincideBusqueda(q, i.nombre, i.codigo, i.marca, i.modelo);
    });
  }, [insumosQ.data, q, categoriaFiltro, estado, soloBajoStock, alertaPorInsumo]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Insumos"
        icon={Package}
        subtitle={insumosQ.data && (
          <span>
            ({filtrados.length}
            {filtrados.length !== total ? ` de ${total}` : ""})
          </span>
        )}
        actions={
          <>
            <Button
              variant="secondary"
              icon={Tags}
              onClick={() => setCategoriasOpen(true)}
            >
              Categorías
            </Button>
            <Button variant="secondary" icon={FileUp} onClick={() => setImportando(true)}>
              Importar Excel
            </Button>
            <Button icon={Plus} onClick={() => setEditing("new")}>
              Nuevo insumo
            </Button>
          </>
        }
      />

      {importando && (
        <ImportarPlanillaModal
          titulo="Importar insumos y stock inicial"
          descripcion={
            <>
              Carga el catálogo y lo que hay hoy en bodega. Obligatoria: <b>codigo</b>. Con{" "}
              <b>cantidad</b> y <b>costo_unitario</b> se carga el stock inicial en la bodega
              indicada, sólo si esa bodega aún no tiene movimientos del insumo: subir dos veces
              el archivo no duplica stock. Categorías y bodegas nuevas se crean solas.
            </>
          }
          importar={importarInsumos}
          descargarPlantilla={descargarPlantillaInsumos}
          detalle={(r) => [
            ...(r.stock_cargado > 0 ? [`Stock inicial en ${r.stock_cargado} filas`] : []),
            ...(r.stock_omitido > 0
              ? [`${r.stock_omitido} filas con stock no se cargan (ya hay movimientos)`]
              : []),
            ...(r.categorias_creadas.length > 0
              ? [`Categorías nuevas: ${r.categorias_creadas.join(", ")}`]
              : []),
            ...(r.bodegas_creadas.length > 0
              ? [`Bodegas nuevas: ${r.bodegas_creadas.join(", ")}`]
              : []),
          ]}
          onClose={() => setImportando(false)}
          onImportado={() => {
            void qc.invalidateQueries({ queryKey: ["inventario"] });
          }}
        />
      )}

      <p className="text-sm text-neutral-500">
        El catálogo de materiales de bodega (cloro, tubería, EPP...). El costo
        promedio (PPP) se calcula solo con cada entrada de compra; acá no se
        edita.
      </p>

      {total > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar nombre, código, marca…"
              className="w-72 rounded-md border border-neutral-450 py-1.5 pl-8 pr-3 text-sm shadow-sm"
            />
          </div>
          <select
            value={categoriaFiltro}
            onChange={(e) => setCategoriaFiltro(e.target.value)}
            className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
          >
            <option value="">Todas las categorías</option>
            {categorias.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.nombre}
              </option>
            ))}
          </select>
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value as FiltroEstado)}
            className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
          >
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
            <option value="todos">Todos</option>
          </select>
          <label
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm shadow-sm transition ${
              soloBajoStock
                ? "border-amber-300 bg-amber-50 text-amber-800"
                : "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50"
            }`}
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-neutral-450 text-amber-600 focus:ring-amber-500"
              checked={soloBajoStock}
              onChange={(e) => setSoloBajoStock(e.target.checked)}
            />
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.75} />
            Solo bajo stock
            {nBajoStock > 0 && (
              <span className="tabular-nums font-medium">({nBajoStock})</span>
            )}
          </label>
        </div>
      )}

      {editing && (
        <InsumoForm
          insumo={editing === "new" ? null : editing}
          categorias={categorias}
          onClose={() => setEditing(null)}
        />
      )}

      {categoriasOpen && (
        <CategoriasModal
          categorias={categorias}
          onClose={() => setCategoriasOpen(false)}
        />
      )}

      {insumosQ.isPending && <Cargando />}
      {insumosQ.isError && (
        <ErrorCarga
          error={insumosQ.error}
          mensaje="Error al cargar insumos."
          onReintentar={() => insumosQ.refetch()}
        />
      )}

      {insumosQ.data && total === 0 && (
        <EmptyState
          icon={Package}
          title="Sin insumos todavía"
          description="Carga los materiales que maneja la bodega para poder registrar entradas y salidas."
        >
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Nuevo insumo
          </Button>
        </EmptyState>
      )}

      {total > 0 && filtrados.length === 0 && (
        <p className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-6 text-center text-sm text-neutral-500">
          Ningún insumo coincide con el filtro.
        </p>
      )}

      {filtrados.length > 0 && (
        <TableWrap label="Insumos">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Nombre</th>
                <th scope="col" className="px-3 py-2 text-left">Código</th>
                <th scope="col" className="px-3 py-2 text-left">Categoría</th>
                <th scope="col" className="px-3 py-2 text-left">Unidad</th>
                <th scope="col" className="px-3 py-2 text-right">Costo prom.</th>
                <th scope="col" className="px-3 py-2 text-right">Mín / Crít</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtrados.map((i) => (
                <InsumoRow
                  key={i.id}
                  insumo={i}
                  categoriaNombre={
                    i.categoria_id != null
                      ? nombrePorId.get(i.categoria_id) ?? "—"
                      : "—"
                  }
                  alerta={alertaPorInsumo.get(i.id)}
                  onEdit={() => setEditing(i)}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function InsumoRow({
  insumo,
  categoriaNombre,
  alerta,
  onEdit,
}: {
  insumo: InsumoRead;
  categoriaNombre: string;
  alerta?: AlertaStock | undefined;
  onEdit: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const toggle = useMutation({
    mutationFn: (activo: boolean) => actualizarInsumo(insumo.id, { activo }),
    onSuccess: (_, activo) => {
      toast.success(
        activo
          ? `Insumo "${insumo.nombre}" reactivado`
          : `Insumo "${insumo.nombre}" desactivado`,
      );
      void qc.invalidateQueries({ queryKey: ["inventario", "insumos"] });
      setConfirming(false);
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(err instanceof ApiError ? err.message : "No se pudo actualizar");
    },
  });

  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-3 py-2 font-medium text-neutral-900">
        {insumo.nombre}
        {(insumo.marca || insumo.modelo) && (
          <div className="text-xs text-neutral-500 font-normal">
            {[insumo.marca, insumo.modelo].filter(Boolean).join(" · ")}
          </div>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-neutral-700">
        {insumo.codigo ?? "—"}
      </td>
      <td className="px-3 py-2 text-neutral-600">{categoriaNombre}</td>
      <td className="px-3 py-2 text-neutral-600">{insumo.unidad_medida}</td>
      <td className="px-3 py-2 text-right tabular-nums text-neutral-700">
        {formatCLP(insumo.costo_promedio)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-neutral-600">
        <div className="flex items-center justify-end gap-2">
          {alerta && (
            <Badge tone={alerta.nivel === "critico" ? "rose" : "amber"}>
              {alerta.nivel === "critico" ? "Crítico" : "Bajo"}:{" "}
              {Number(alerta.cantidad_total)} {alerta.unidad_medida}
            </Badge>
          )}
          <span>
            {Number(insumo.stock_minimo)} / {Number(insumo.stock_critico)}
          </span>
        </div>
      </td>
      <td className="px-3 py-2">
        <Badge tone={insumo.activo ? "emerald" : "slate"}>
          {insumo.activo ? "Activo" : "Inactivo"}
        </Badge>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-3">
          <Button variant="link" size="sm" icon={Pencil} onClick={onEdit}>
            Editar
          </Button>
          {insumo.activo ? (
            <Button
              variant="linkDanger"
              size="sm"
              icon={Power}
              onClick={() => setConfirming(true)}
              disabled={toggle.isPending}
            >
              Desactivar
            </Button>
          ) : (
            <Button
              variant="link"
              size="sm"
              icon={Power}
              onClick={() => toggle.mutate(true)}
              disabled={toggle.isPending}
            >
              Activar
            </Button>
          )}
        </div>
      </td>

      {confirming && (
        <ConfirmDialog
          open
          title={`Desactivar insumo "${insumo.nombre}"`}
          description="Deja de aparecer en el catálogo activo. No se elimina: se puede reactivar cuando quieras."
          confirmLabel="Desactivar"
          tone="danger"
          loading={toggle.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => toggle.mutate(false)}
        />
      )}
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Form alta/edición de insumo
// ────────────────────────────────────────────────────────────────────────

function InsumoForm({
  insumo,
  categorias,
  onClose,
}: {
  insumo: InsumoRead | null;
  categorias: CategoriaRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = insumo !== null;

  const [nombre, setNombre] = useState(insumo?.nombre ?? "");
  const [categoriaId, setCategoriaId] = useState(
    insumo?.categoria_id != null ? String(insumo.categoria_id) : "",
  );
  const [codigo, setCodigo] = useState(insumo?.codigo ?? "");
  const [unidad, setUnidad] = useState(insumo?.unidad_medida ?? "Un");
  const [stockMinimo, setStockMinimo] = useState(
    insumo != null ? String(Number(insumo.stock_minimo)) : "0",
  );
  const [stockCritico, setStockCritico] = useState(
    insumo != null ? String(Number(insumo.stock_critico)) : "0",
  );
  const [marca, setMarca] = useState(insumo?.marca ?? "");
  const [modelo, setModelo] = useState(insumo?.modelo ?? "");
  const [descripcion, setDescripcion] = useState(insumo?.descripcion ?? "");
  const [error, setError] = useState<string | null>(null);

  const crearMut = useMutation({
    mutationFn: (p: InsumoCreate) => crearInsumo(p),
    onSuccess: (i) => {
      toast.success(`Insumo "${i.nombre}" creado`);
      void qc.invalidateQueries({ queryKey: ["inventario", "insumos"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: InsumoUpdate) =>
      actualizarInsumo((insumo as InsumoRead).id, p),
    onSuccess: (i) => {
      toast.success(`Insumo "${i.nombre}" actualizado`);
      void qc.invalidateQueries({ queryKey: ["inventario", "insumos"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;

  function submit(): void {
    setError(null);
    if (!nombre.trim()) return setError("Nombre requerido");

    const min = Number(stockMinimo);
    const crit = Number(stockCritico);
    if (!Number.isFinite(min) || min < 0)
      return setError("Stock mínimo debe ser un número ≥ 0");
    if (!Number.isFinite(crit) || crit < 0)
      return setError("Stock crítico debe ser un número ≥ 0");

    const payload = {
      nombre: nombre.trim(),
      categoria_id: categoriaId === "" ? null : Number(categoriaId),
      codigo: codigo.trim() || null,
      unidad_medida: unidad,
      stock_minimo: min,
      stock_critico: crit,
      marca: marca.trim() || null,
      modelo: modelo.trim() || null,
      descripcion: descripcion.trim() || null,
    };
    if (editando) actualizarMut.mutate(payload);
    else crearMut.mutate(payload);
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    nombre, categoriaId, codigo, unidad, stockMinimo, stockCritico, marca, modelo,
    descripcion,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crearMut.isPending || actualizarMut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={editando ? "Editar insumo" : "Nuevo insumo"}
      subtitle={editando ? insumo.nombre : undefined}
      icon={Package}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? editando
                ? "Guardando…"
                : "Creando…"
              : editando
                ? "Guardar cambios"
                : "Crear insumo"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="Nombre"
          required
          type="text"
          maxLength={120}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          disabled={isPending}
          autoFocus
        />
        <Select
          label="Categoría"
          value={categoriaId}
          onChange={(e) => setCategoriaId(e.target.value)}
          disabled={isPending}
        >
          <option value="">— Sin categoría —</option>
          {categorias.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.nombre}
            </option>
          ))}
        </Select>
        <Input
          label="Código"
          type="text"
          maxLength={40}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          disabled={isPending}
          placeholder="ej. CLR-25"
        />
        <Select
          label="Unidad de medida"
          value={unidad}
          onChange={(e) => setUnidad(e.target.value)}
          disabled={isPending}
        >
          {UNIDADES.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </Select>
        <Input
          label="Stock mínimo"
          type="number"
          min={0}
          value={stockMinimo}
          onChange={(e) => setStockMinimo(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Stock crítico"
          type="number"
          min={0}
          value={stockCritico}
          onChange={(e) => setStockCritico(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Marca"
          type="text"
          maxLength={80}
          value={marca}
          onChange={(e) => setMarca(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Modelo"
          type="text"
          maxLength={80}
          value={modelo}
          onChange={(e) => setModelo(e.target.value)}
          disabled={isPending}
        />
        <div className="md:col-span-2">
          <Textarea
            label="Descripción"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            disabled={isPending}
            rows={2}
          />
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Modal de gestión de categorías
// ────────────────────────────────────────────────────────────────────────

function CategoriasModal({
  categorias,
  onClose,
}: {
  categorias: CategoriaRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const [editing, setEditing] = useState<CategoriaRead | null>(null);
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [requiereSic, setRequiereSic] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset(): void {
    setEditing(null);
    setNombre("");
    setDescripcion("");
    setRequiereSic(false);
    setError(null);
  }

  function cargar(cat: CategoriaRead): void {
    setEditing(cat);
    setNombre(cat.nombre);
    setDescripcion(cat.descripcion ?? "");
    setRequiereSic(cat.requiere_sic_siempre);
    setError(null);
  }

  const crearMut = useMutation({
    mutationFn: (p: CategoriaCreate) => crearCategoria(p),
    onSuccess: (c) => {
      toast.success(`Categoría "${c.nombre}" creada`);
      void qc.invalidateQueries({ queryKey: ["inventario", "categorias"] });
      reset();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: CategoriaUpdate) =>
      actualizarCategoria((editing as CategoriaRead).id, p),
    onSuccess: (c) => {
      toast.success(`Categoría "${c.nombre}" actualizada`);
      void qc.invalidateQueries({ queryKey: ["inventario", "categorias"] });
      reset();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;

  function submit(): void {
    setError(null);
    if (!nombre.trim()) return setError("Nombre requerido");
    const payload = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim() || null,
      requiere_sic_siempre: requiereSic,
    };
    if (editing) actualizarMut.mutate(payload);
    else crearMut.mutate(payload);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Categorías de insumos"
      icon={Tags}
      size="md"
    >
      <div className="space-y-4">
        {categorias.length === 0 ? (
          <p className="text-sm text-neutral-500">Todavía no hay categorías.</p>
        ) : (
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {categorias.map((c) => (
              <li
                key={c.id}
                className="flex items-start justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-neutral-900">{c.nombre}</span>
                    {c.requiere_sic_siempre && (
                      <Badge tone="amber">SIC siempre</Badge>
                    )}
                  </div>
                  {c.descripcion && (
                    <p className="text-xs text-neutral-500">{c.descripcion}</p>
                  )}
                </div>
                <Button
                  variant="link"
                  size="sm"
                  icon={Pencil}
                  onClick={() => cargar(c)}
                  disabled={isPending}
                >
                  Editar
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
            {editing ? `Editar "${editing.nombre}"` : "Nueva categoría"}
          </p>
          <Input
            label="Nombre"
            required
            type="text"
            maxLength={80}
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            disabled={isPending}
          />
          <Input
            label="Descripción"
            type="text"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            disabled={isPending}
          />
          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-neutral-450 text-brand-600 focus:ring-brand-500"
              checked={requiereSic}
              onChange={(e) => setRequiereSic(e.target.checked)}
              disabled={isPending}
            />
            Exige SIC siempre (ej. químicos regulados)
          </label>

          {error && <p className="text-sm text-rose-700">{error}</p>}

          <div className="flex items-center justify-end gap-2">
            {editing && (
              <Button variant="secondary" onClick={reset} disabled={isPending}>
                Cancelar
              </Button>
            )}
            <Button onClick={submit} disabled={isPending}>
              {isPending
                ? "Guardando…"
                : editing
                  ? "Guardar cambios"
                  : "Crear categoría"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
